
  import { defineComponent, computed, onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import Highcharts, { ExportingMimeTypeValue } from 'highcharts';
  import { Chart } from 'highcharts-vue';
  import { Options, PointWithMeta } from '@/ui/concerns/analytics/useCharts';
  import emitter from '@/services/emitter';
  import {
    offlineFormatsWhiteList,
    SupportedExportFormat,
    supportedMimeTypes,
  } from '@/services/api/resources/analytics';
  import { ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import { useAttributes } from '@/ui/concerns/analytics/widget';
  import { AllAttributesType } from '@/services/api/resources/analytics-builder/constants';
  import { t } from '@/ui/i18n/i18n';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';

  interface ChartProps {
    options: Options;
    widget: ReportWidget;
  }

  export const defaultColors = [
    '#00c389',
    '#00b2ff',
    '#fd2f58',
    '#ffad22',
    '#8d5fd6',
    '#00edff',
    '#ffec99',
    '#60d2c9',
    '#f5d400',
    '#ffbc6e',
    '#8dcfff',
    '#fe7ce4',
    '#ffd5e8',
    '#8df4ff',
    '#ff6e44',
  ];

  export default defineComponent<ChartProps>({
    components: {
      HighchartsVue: Chart,
    },
    props: ['options', 'widget'],
    setup(props) {
      const chart = ref<Chart | null>(null);
      const { isGuest } = useReportInjection();

      const chartOptions = computed<Highcharts.Options>(() => ({
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        exporting: {
          enabled: false,
          fallbackToExportServer: false,
        },
        tooltip: {
          shared: true,
          formatter() {
            const { head, attribute, value } = (this.point as PointWithMeta).meta;

            if (!this.points) {
              return `<b>${useAttributes().allAttributeTypeLabels[attribute as AllAttributesType]}: ${head}</b><br/><br/>${this.series.name}: ${value}`;
            }

            let tip = this.points.reduce((acc, item) => {
              const { value } = (item.point as PointWithMeta).meta;
              return `${acc}<br/>${item.series.name}: ${value}`;
            }, `<b>${useAttributes().allAttributeTypeLabels[attribute as AllAttributesType]}: ${head}</b><br/>`);

            if (isGuest && props.options.chart?.type === 'spline') {
              tip += `<br/><br/>${t('analytics.chart.click-and-drag-to-zoom')}`;
            }

            return tip;
          },
        },
        colors: defaultColors,
        ...props.options,
      }));

      Highcharts.setOptions({
        lang: {
          resetZoom: t('analytics.chart.reset-zoom'),
        },
      });

      const exportChart = (event: {
        key: string;
        format: SupportedExportFormat;
      }) => {
        if (event.key !== props.widget.key || !offlineFormatsWhiteList.includes(event.format)) {
          return;
        }

        const mimeType = supportedMimeTypes[event.format];

        chart.value?.chart?.exportChartLocal({
          type: mimeType as ExportingMimeTypeValue,
          filename: 'chart',
        }, {});
      };

      const fullScreen = (event: { key: string }) => {
        if (event.key !== props.widget.key || !chart.value?.chart?.fullscreen) {
          return;
        }

        chart.value.chart.fullscreen.open();
      };

      const print = (event: { key: string }) => {
        if (event.key !== props.widget.key) {
          return;
        }

        chart.value?.chart?.print();
      };

      onBeforeMount(() => {
        emitter.on('analyticsExportWidget', exportChart);
        emitter.on('analyticsChartFullScreen', fullScreen);
        emitter.on('analyticsChartPrint', print);
      });

      onBeforeUnmount(() => {
        emitter.off('analyticsExportWidget', exportChart);
        emitter.off('analyticsChartFullScreen', fullScreen);
        emitter.off('analyticsChartPrint', print);
      });

      return {
        chart,
        chartOptions,
      };
    },
  });
